import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import customerRideApi from 'api/customerRideApi';

const formatTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('vi-VN', {
    hour: '2-digit', minute: '2-digit',
  }).format(date);
};

const formatDateFull = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return 'Hôm nay';
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Hôm qua';
  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(date);
};

const formatRelativeTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return formatTime(date);
  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit', month: '2-digit',
  }).format(date);
};

const conversationId = (item) => item?.id || item?.conversationId;
const extractMessages = (response) => response?.messages || response?.data?.messages || [];

const normalizeSupportConversation = (response) => {
  const conversation = response?.conversation || response?.data?.conversation;
  if (!conversation?.id) return null;
  return {
    id: conversation.id,
    type: 'SYSTEM',
    isSupport: true,
    counterpart: { userId: 0, name: 'Hỗ trợ EVG' },
    trip: { tripCode: '', originAddress: '', destinationAddress: '', seats: 1 },
    lastMessageText: conversation.lastMessageText,
    lastMessageAt: conversation.lastMessageAt,
    unreadCount: Number(conversation.unreadCount || 0),
    state: 'ACTIVE',
  };
};

const RideMessagesView = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState([]);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const messagesEndRef = useRef(null);
  const conversationsRef = useRef([]);
  const threadRequestRef = useRef(0);

  // Keep async polling callbacks pointed at the latest list. Without this
  // ref, every refresh recreates loadThread and can race a conversation the
  // customer just opened.
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await customerRideApi.getConversations('CUSTOMER');
      const rideItems = response?.conversations || response?.data?.conversations || [];
      // Support is a synthetic conversation loaded from a separate endpoint;
      // keep the already-open support row while the driver list is polling so
      // it does not briefly disappear and trigger a thread fallback.
      const existingSupport = conversationsRef.current.find((item) => item.isSupport);
      const nextConversations = existingSupport
        ? [existingSupport, ...rideItems.filter((item) => conversationId(item) !== existingSupport.id)]
        : rideItems;
      setConversations(nextConversations);
      setSelectedId((id) => {
        const selectedStillExists = nextConversations.some((item) => conversationId(item) === id);
        const selectedSupportStillExists = conversationsRef.current.some(
          (item) => conversationId(item) === id && item.isSupport,
        );
        // Polling may return the driver list without the synthetic support
        // conversation. Preserve whichever thread is open instead of
        // jumping back to the first driver every 8 seconds.
        // Opening the Messages tab must show the inbox first, especially on
        // mobile where an active conversation replaces the whole list.
        return id && (selectedStillExists || selectedSupportStillExists) ? id : null;
      });

      customerRideApi.getSupportConversation().then((supportResponse) => {
        const support = normalizeSupportConversation(supportResponse);
        if (!support) return;
        setConversations((current) => [
          support,
          ...current.filter((item) => conversationId(item) !== support.id),
        ]);
      }).catch(() => {});

      customerRideApi.syncConversations().catch(() => {});
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Không thể tải tin nhắn. Vui lòng thử lại.');
      if (!silent) setConversations([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
    const timer = window.setInterval(() => loadConversations({ silent: true }), 8000);
    return () => window.clearInterval(timer);
  }, [loadConversations]);

  const selected = useMemo(
    () => conversations.find((item) => conversationId(item) === selectedId),
    [conversations, selectedId],
  );

  const decorateMessages = useCallback((items, activeConversation) => items.map((message) => {
    const isMine = activeConversation?.isSupport
      ? message.senderRole === 'USER'
      : Number(message.senderId) !== Number(activeConversation?.counterpart?.userId);
    return { ...message, isMine };
  }), []);

  const loadThread = useCallback(async (conversationIdToLoad, { silent = false } = {}) => {
    const requestToken = ++threadRequestRef.current;
    if (!conversationIdToLoad) { setThread([]); return; }
    const activeConversation = conversationsRef.current.find(
      (item) => conversationId(item) === conversationIdToLoad,
    );
    if (!activeConversation) return;
    if (!silent) setThreadLoading(true);
    try {
      const response = activeConversation.isSupport
        ? await customerRideApi.getSupportConversationMessages(conversationIdToLoad, { pageSize: 50 })
        : await customerRideApi.getConversationMessages(conversationIdToLoad, { pageSize: 50 });
      // A customer can switch threads while the previous request is in
      // flight. Never let the old response replace the newly selected thread.
      if (requestToken !== threadRequestRef.current) return;
      setThread(decorateMessages(extractMessages(response), activeConversation));
      if (activeConversation.isSupport) await customerRideApi.markSupportConversationRead(conversationIdToLoad);
      else await customerRideApi.markConversationRead(conversationIdToLoad);
      setConversations((current) => {
        let changed = false;
        const next = current.map((item) => {
          if (conversationId(item) !== conversationIdToLoad || Number(item.unreadCount || 0) === 0) return item;
          changed = true;
          return { ...item, unreadCount: 0 };
        });
        return changed ? next : current;
      });
    } catch (requestError) {
      if (!silent && requestToken === threadRequestRef.current) {
        setError(requestError?.response?.data?.message || 'Không thể tải nội dung hội thoại.');
        setThread([]);
      }
    } finally {
      if (!silent && requestToken === threadRequestRef.current) setThreadLoading(false);
    }
  }, [decorateMessages]);

  useEffect(() => {
    loadThread(selectedId);
  }, [selectedId, loadThread]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const timer = window.setInterval(() => loadThread(selectedId, { silent: true }), 8000);
    return () => window.clearInterval(timer);
  }, [selectedId, loadThread]);

  useEffect(() => {
    scrollToBottom(thread.length > 10 ? 'auto' : 'smooth');
  }, [thread]);

  const visible = conversations.filter((item) => {
    const type = (item.type || (item.isSupport ? 'SYSTEM' : 'CUSTOMER')).toLowerCase();
    if (filter !== 'all' && filter !== type) return false;
    const haystack = `${item.counterpart?.name || ''} ${item.lastMessageText || ''} ${item.trip?.tripCode || ''} ${item.trip?.originAddress || ''} ${item.trip?.destinationAddress || ''}`.toLowerCase();
    return !query.trim() || haystack.includes(query.trim().toLowerCase());
  });

  const send = async (event) => {
    event.preventDefault();
    const text = input.trim();
    if (!text || !selectedId || sending || selected?.state === 'READ_ONLY') return;
    const clientMessageId = `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimistic = {
      id: clientMessageId,
      clientMessageId,
      text,
      sentAt: new Date().toISOString(),
      isMine: true,
      status: 'SENDING',
    };
    setThread((current) => [...current, optimistic]);
    setInput('');
    setSending(true);
    try {
      const response = selected.isSupport
        ? await customerRideApi.sendSupportMessage(selectedId, { text, clientMessageId, senderContext: 'CUSTOMER' })
        : await customerRideApi.sendConversationMessage(selectedId, { text, clientMessageId });
      const message = response?.message || response?.data?.message || optimistic;
      setThread((current) => current.map((item) => (item.id === optimistic.id ? { ...message, isMine: true } : item)));
      setConversations((current) => current.map((item) => (conversationId(item) === selectedId
        ? {
          ...item,
          lastMessageText: text,
          lastMessageAt: new Date().toISOString(),
          lastMessageIsMine: true,
        }
        : item)));
    } catch (requestError) {
      setThread((current) => current.map((item) => (item.id === optimistic.id ? { ...item, status: 'FAILED' } : item)));
      setError(requestError?.response?.data?.message || 'Gửi tin nhắn không thành công.');
    } finally {
      setSending(false);
    }
  };

  const totalUnread = useMemo(
    () => conversations.reduce((acc, c) => acc + (Number(c.unreadCount) || 0), 0),
    [conversations],
  );

  // Group messages by date for clean separator pills
  const groupedThread = useMemo(() => {
    const groups = [];
    let currentDate = null;
    let currentGroup = [];

    thread.forEach((msg) => {
      const dateLabel = formatDateFull(msg.sentAt);
      if (dateLabel !== currentDate) {
        if (currentGroup.length > 0) {
          groups.push({ date: currentDate, messages: currentGroup });
        }
        currentDate = dateLabel;
        currentGroup = [msg];
      } else {
        currentGroup.push(msg);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ date: currentDate, messages: currentGroup });
    }

    return groups;
  }, [thread]);

  // Track the most recent message timestamp from the counterpart to determine delivered/read status
  const lastTheirsMessageAt = useMemo(() => {
    let latest = null;
    thread.forEach((msg) => {
      const isMine = msg.isMine || msg.lastMessageIsMine;
      if (!isMine && msg.sentAt) {
        if (!latest || new Date(msg.sentAt) > new Date(latest)) {
          latest = msg.sentAt;
        }
      }
    });
    return latest;
  }, [thread]);

  return (
    <section id="messages" className="section active customer-ride-messages-view">
      <div className="page-header">
        <div>
          <div className="page-title-row">
            <h1>Tin nhắn</h1>
            {totalUnread > 0 && <span className="total-unread-badge">{totalUnread} mới</span>}
          </div>
          <p>Trao đổi với tài xế và đội ngũ hỗ trợ EVG trên cùng tài khoản.</p>
        </div>
        <button
          type="button"
          className="customer-ride-refresh-btn"
          onClick={() => loadConversations()}
          title="Tải lại danh sách tin nhắn"
        >
          <i className="fa-solid fa-arrows-rotate" /> Làm mới
        </button>
      </div>

      {error && (
        <div className="ride-inline-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" /> {error}
          <button type="button" onClick={() => loadConversations()}>Thử lại</button>
        </div>
      )}

      <div className={`panel message-wrap ${selectedId ? 'has-active-chat' : ''}`}>
        {/* Left Inbox Column */}
        <div className="inbox">
          <div className="inbox-controls">
            <div className="msgsearch">
              <i className="fa-solid fa-magnifying-glass" />
              <input
                placeholder="Tìm tin nhắn, tài xế, chuyến đi..."
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              {query && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => setQuery('')}
                  aria-label="Xóa tìm kiếm"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              )}
            </div>

            <div className="seg">
              {[
                ['all', 'Tất cả'],
                ['customer', 'Tài xế'],
                ['system', 'Hệ thống'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={filter === value ? 'active' : ''}
                  onClick={() => setFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="messages">
            {loading ? (
              <div className="ride-list-state">
                <i className="fa-solid fa-spinner fa-spin" /> Đang tải tin nhắn...
              </div>
            ) : visible.length === 0 ? (
              <div className="ride-list-state empty">
                <div className="empty-icon-wrap">
                  <i className="fa-regular fa-comments" />
                </div>
                <strong>Không tìm thấy hội thoại nào</strong>
                <p>{query ? 'Thử tìm với từ khóa khác' : 'Bạn chưa có cuộc trò chuyện nào'}</p>
              </div>
            ) : (
              visible.map((item) => {
                const id = conversationId(item);
                const system = item.isSupport || (item.type || '').toUpperCase() === 'SYSTEM';
                const isSelected = id === selectedId;
                const unread = Number(item.unreadCount || 0);

                return (
                  <button
                    type="button"
                    key={id}
                    className={`message ${isSelected ? 'active' : ''} ${unread > 0 ? 'has-unread' : ''}`}
                    onClick={() => setSelectedId(id)}
                  >
                    <div className={`message-avatar ${system ? 'system' : 'driver'}`}>
                      <i className={`fa-solid ${system ? 'fa-headset' : 'fa-user'}`} />
                    </div>

                    <div className="message-body">
                      <div className="message-head">
                        <strong className="counterpart-name">
                          {item.counterpart?.name || (system ? 'Hỗ trợ EVG' : 'Tài xế EVG')}
                        </strong>
                        <span className="message-time">
                          {formatRelativeTime(item.lastMessageAt)}
                        </span>
                      </div>

                      <div className="message-snippet">
                        {item.lastMessageIsMine && <span className="snippet-mine">Bạn: </span>}
                        {item.lastMessageText || 'Bắt đầu cuộc trò chuyện'}
                      </div>

                      <div className="message-meta">
                        <span className={`tag ${system ? 'system' : 'trip'}`}>
                          {system ? (
                            <>
                              <i className="fa-solid fa-shield-halved" /> Hệ thống
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-car" /> {item.trip?.tripCode || 'Chuyến xe'}
                            </>
                          )}
                        </span>

                        {unread > 0 && <span className="unread-badge">{unread}</span>}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right Thread Column */}
        <div className="thread">
          {!selected ? (
            <div className="ride-list-state empty select-prompt">
              <div className="empty-icon-wrap">
                <i className="fa-regular fa-message" />
              </div>
              <strong>Chọn một cuộc trò chuyện</strong>
              <p>Chọn liên hệ từ danh sách bên trái để bắt đầu trao đổi</p>
            </div>
          ) : (
            <>
              {/* Thread Header */}
              <div className="thread-header">
                <div className="thread-header-left">
                  <button
                    type="button"
                    className="mobile-back-to-inbox-btn"
                    onClick={() => setSelectedId(null)}
                    title="Quay lại danh sách"
                  >
                    <i className="fa-solid fa-chevron-left" />
                  </button>
                  <div className={`thread-avatar ${selected.isSupport ? 'system' : 'driver'}`}>
                    <i className={`fa-solid ${selected.isSupport ? 'fa-headset' : 'fa-user'}`} />
                  </div>
                  <div className="thread-user-info">
                    <h3>
                      {selected.isSupport ? 'Hỗ trợ EVG' : (selected.counterpart?.name || 'Tài xế EVG')}
                    </h3>
                    <p>
                      {selected.isSupport
                        ? 'Đội ngũ chăm sóc khách hàng EVG Group'
                        : (selected.trip?.originAddress && selected.trip?.destinationAddress
                          ? `${selected.trip.originAddress} → ${selected.trip.destinationAddress}`
                          : 'Cuộc trò chuyện chuyến đi')}
                    </p>
                  </div>
                </div>

                {!selected.isSupport && selected.trip?.tripCode && (
                  <span className="thread-trip-code-badge">
                    <i className="fa-solid fa-car" /> Mã chuyến: {selected.trip.tripCode}
                  </span>
                )}
              </div>

              {/* Chat Trip Context Card (matching Flutter ChatTripContextCard) */}
              {!selected.isSupport && selected.trip && (selected.trip.originAddress || selected.trip.tripCode) && (
                <div className="chat-trip-context-card">
                  <div className="context-card-inner">
                    <div className="context-route-row">
                      <div className="context-route-marker">
                        <i className="fa-solid fa-route" />
                      </div>
                      <div className="context-route-text">
                        <span className="route-stop pickup">{selected.trip.originAddress || 'Điểm đón'}</span>
                        <i className="fa-solid fa-arrow-right-long route-arrow" />
                        <span className="route-stop dropoff">{selected.trip.destinationAddress || 'Điểm trả'}</span>
                      </div>
                    </div>

                    <div className="context-meta-row">
                      {selected.trip.tripCode && (
                        <span className="meta-item trip-code">
                          <i className="fa-solid fa-ticket" /> {selected.trip.tripCode}
                        </span>
                      )}
                      {selected.trip.seats && (
                        <span className="meta-item seats">
                          <i className="fa-solid fa-user-group" /> {selected.trip.seats} ghế
                        </span>
                      )}
                      {selected.trip.departureAt && (
                        <span className="meta-item time">
                          <i className="fa-regular fa-clock" /> {formatRelativeTime(selected.trip.departureAt)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Thread Messages Body */}
              <div className="thread-body">
                {threadLoading ? (
                  <div className="ride-list-state">
                    <i className="fa-solid fa-spinner fa-spin" /> Đang tải hội thoại...
                  </div>
                ) : thread.length === 0 ? (
                  <div className="ride-list-state empty">
                    <div className="empty-icon-wrap">
                      <i className="fa-regular fa-paper-plane" />
                    </div>
                    <strong>Chưa có tin nhắn nào</strong>
                    <p>Hãy gửi lời chào đầu tiên để bắt đầu cuộc trò chuyện!</p>
                  </div>
                ) : (
                  groupedThread.map((group) => (
                    <div key={group.date} className="thread-date-group">
                      <div className="chat-date-separator">
                        <span>{group.date}</span>
                      </div>

                      {group.messages.map((message) => {
                        const isSystem = message.kind === 'SYSTEM' || message.senderRole === 'SYSTEM';
                        const isMine = message.isMine || message.lastMessageIsMine;
                        const isFailed = message.status === 'FAILED';
                        const isSending = message.status === 'SENDING';
                        const isDelivered = Boolean(
                          message.status === 'DELIVERED' ||
                          message.status === 'READ' ||
                          message.status === 'SEEN' ||
                          message.isDelivered ||
                          message.deliveredAt ||
                          message.readAt ||
                          (selected?.counterpartLastReadAt && message.sentAt && new Date(selected.counterpartLastReadAt) >= new Date(message.sentAt)) ||
                          (lastTheirsMessageAt && message.sentAt && new Date(lastTheirsMessageAt) >= new Date(message.sentAt))
                        );

                        if (isSystem) {
                          return (
                            <div key={message.id || message.clientMessageId} className="chat-system-message">
                              <i className="fa-solid fa-circle-info" />
                              <span>{message.text}</span>
                              <small>{formatTime(message.sentAt)}</small>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={message.id || message.clientMessageId}
                            className={`chat-bubble-row ${isMine ? 'mine' : 'theirs'} ${isFailed ? 'failed' : ''}`}
                          >
                            <div className="bubble">
                              <div className="bubble-text">{message.text}</div>
                              <div className="bubble-footer">
                                <span className="bubble-time">{formatTime(message.sentAt)}</span>
                                {isMine && (
                                  <span
                                    className="bubble-status"
                                    title={
                                      isFailed
                                        ? 'Lỗi gửi tin nhắn'
                                        : isSending
                                          ? 'Đang gửi...'
                                          : isDelivered
                                            ? 'Đã nhận'
                                            : 'Đã gửi'
                                    }
                                  >
                                    {isFailed ? (
                                      <span className="status-failed">
                                        <i className="fa-solid fa-circle-exclamation" /> Lỗi gửi
                                      </span>
                                    ) : isSending ? (
                                      <i className="fa-solid fa-clock sending-spin" />
                                    ) : isDelivered ? (
                                      <i className="fa-solid fa-check-double status-delivered" />
                                    ) : (
                                      <i className="fa-solid fa-check status-sent" />
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Composer / Read-only notice */}
              {selected.state === 'READ_ONLY' ? (
                <div className="chatbox-readonly">
                  <i className="fa-solid fa-lock" /> Cuộc trò chuyện này đã kết thúc sau khi chuyến đi hoàn tất.
                </div>
              ) : (
                <form className="chatbox" onSubmit={send}>
                  <input
                    placeholder="Nhập tin nhắn..."
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                  />
                  <button
                    className="send-btn"
                    type="submit"
                    disabled={sending || !input.trim()}
                    aria-label="Gửi tin nhắn"
                  >
                    {sending ? (
                      <i className="fa-solid fa-spinner fa-spin" />
                    ) : (
                      <i className="fa-solid fa-paper-plane" />
                    )}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default RideMessagesView;
